import { useCallback, useState } from 'react';
import Cookie from 'js-cookie';
import moment from 'moment';
import { useTranslations } from '@lang';
import type { Property } from 'shared-types/Property';
import { isAnnouncementLink } from 'shared-utils';
import { useAnnouncements } from './queries';
import type { Announcement } from './schemas';

const COOKIE_NAME = 'announcementsDismissed';
const COOKIE_EXPIRY_DAYS = 365;

export type ActiveAnnouncement =
  | {
      type: 'banner' | 'non_dismissible_banner';
      id: string;
      heading?: string;
      body: string;
      dismissible: boolean;
      onDismiss: () => void;
    }
  | {
      type: 'dialog';
      id: string;
      heading: string;
      body: string;
      imageUrl?: string;
      validFor?: string;
      cta?: { label: string; href: string; onClick: () => void };
      open: boolean;
      onClose: () => void;
    }
  | null;

function readDismissedIds(): string[] {
  const cookie = Cookie.get(COOKIE_NAME);
  if (!cookie) return [];
  try {
    const parsed: unknown = JSON.parse(cookie);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

function persistDismissal(id: string) {
  const existing = readDismissedIds();
  if (existing.includes(id)) return;
  Cookie.set(COOKIE_NAME, JSON.stringify([...existing, id]), {
    expires: COOKIE_EXPIRY_DAYS,
    path: '/',
    secure: process.env.NODE_ENV === 'production',
    sameSite: 'lax',
  });
}

function pickAnnouncement(
  announcements: Announcement[] | undefined,
  dismissed: string[]
): Announcement | null {
  if (!announcements?.length) return null;

  const firstDismissibleNotDismissed = announcements.find(
    (a) => a?._id && a.content.type !== 'non_dismissible_banner' && !dismissed.includes(a._id)
  );
  if (firstDismissibleNotDismissed) return firstDismissibleNotDismissed;

  return announcements.find((a) => a?.content.type === 'non_dismissible_banner') ?? null;
}

function formatValidFor(
  start: Announcement['start'],
  end: Announcement['end'],
  template: string
): string | undefined {
  if (!start?.date || !end?.date) return undefined;
  const startMoment = moment(start.date);
  const endMoment = moment(end.date);
  if (!startMoment.isValid() || !endMoment.isValid()) return undefined;

  const startLabel = startMoment.format('D MMMM');
  const endLabel = endMoment.format('D MMMM YYYY');
  return template.replace('{startDate}', startLabel).replace('{endDate}', endLabel);
}

interface UseActiveAnnouncementArgs {
  slug: Property['slug'];
  language?: string;
  fallbackImageUrl?: string | null;
}

export function useActiveAnnouncement({
  slug,
  language,
  fallbackImageUrl,
}: UseActiveAnnouncementArgs): ActiveAnnouncement {
  const { data } = useAnnouncements(slug, language);
  const { t } = useTranslations();
  const [hidden, setHidden] = useState(false);

  const announcement = pickAnnouncement(data?.data, readDismissedIds());

  const dismiss = useCallback((id: string) => {
    persistDismissal(id);
    setHidden(true);
  }, []);

  if (!announcement || hidden) return null;

  const id = announcement._id;
  const { type, heading, body, ctaLabel, ctaUrl } = announcement.content;

  if (type === 'dialog') {
    const label = ctaLabel?.trim();
    const href = ctaUrl?.trim() ?? '';

    return {
      type: 'dialog',
      id,
      heading,
      body,
      imageUrl: fallbackImageUrl ?? undefined,
      validFor: formatValidFor(announcement.start, announcement.end, t('announcement.validFor')),
      cta:
        label && isAnnouncementLink(href) ? { label, href, onClick: () => dismiss(id) } : undefined,
      open: true,
      onClose: () => dismiss(id),
    };
  }

  return {
    type,
    id,
    heading: heading || undefined,
    body,
    dismissible: type !== 'non_dismissible_banner',
    onDismiss: () => dismiss(id),
  };
}
